import React, { useRef } from 'react';
import '../styles/CertificateTemplate.css';
import { formatDate } from '../utils/dateUtils';

export interface CertificateData {
  id: string;
  name: string;
  recipient: string;
  issuer: string;
  issueDate: string;
  expiryDate?: string;
  description: string;
  companyName: string;
  certificateNumber: string;
  authorizedBy: string;
  status: 'valid' | 'expiring' | 'expired';
}

interface CertificateTemplateProps {
  certificateData: CertificateData;
  onDownload?: () => void;
}

const CertificateTemplate: React.FC<CertificateTemplateProps> = ({ 
  certificateData, 
  onDownload 
}) => {
  const certificateRef = useRef<HTMLDivElement>(null);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'valid': return '#10b981';
      case 'expiring': return '#f59e0b';
      case 'expired': return '#ef4444';
      default: return '#6b7280';
    }
  };

  return (
    <div className="certificate-container">
      <div ref={certificateRef} className="certificate-template">
        {/* Security Watermark */}
        <div className="security-watermark">VERIFIED</div>
        
        {/* Certificate Border with decorative pattern */}
        <div className="certificate-border">
          {/* Header Section */}
          <div className="certificate-header">
            <div className="header-left">
              <img 
                src="https://infrasec.se/wp-content/uploads/2024/09/Infrasec_lockup_01@4x.png" 
                alt="Infrasec" 
                className="certificate-logo"
              />
              <div className="issuer-details">
                <div className="issuer-name">INFRASEC AB</div>
                <div className="issuer-tagline">Security • Compliance • Excellence</div>
              </div>
            </div>
            <div className="header-right">
              <div className="certificate-badge">
                <div className="badge-icon">🏆</div>
                <div className="badge-text">CERTIFIED</div>
              </div>
            </div>
          </div>

          {/* Title Section */}
          <div className="certificate-title-section">
            <h1 className="certificate-title">CERTIFICATE OF COMPLIANCE</h1>
            <div className="title-underline"></div>
          </div>

          {/* Main Content */}
          <div className="certificate-content">
            <div className="certificate-main">
              <div className="presentation-text">
                <span className="awarded-prefix">This is to certify that</span>
              </div>
              
              <div className="recipient-section">
                <div className="recipient-name">{certificateData.companyName}</div>
                <div className="recipient-designation">
                  has successfully demonstrated full compliance with
                </div>
              </div>
              
              <div className="certification-focus">
                <div className="certificate-name">{certificateData.name}</div>
                <div className="standards-note">
                  Meeting all required standards and regulatory requirements
                </div>
              </div>
              
              <div className="description-section">
                <p className="description-text">{certificateData.description}</p>
              </div>

              {/* Enhanced Certificate Details */}
              <div className="certificate-details-section">
                <div className="details-grid">
                  <div className="detail-group">
                    <span className="detail-label">Certificate Number</span>
                    <span className="detail-value">{certificateData.certificateNumber}</span>
                  </div>
                  <div className="detail-group">
                    <span className="detail-label">Issue Date</span>
                    <span className="detail-value">{formatDate(certificateData.issueDate)}</span>
                  </div>
                  <div className="detail-group">
                    <span className="detail-label">Valid Until</span>
                    <span className="detail-value">
                      {certificateData.expiryDate ? formatDate(certificateData.expiryDate) : 'No Expiration'}
                    </span>
                  </div>
                  <div className="detail-group">
                    <span className="detail-label">Status</span>
                    <span 
                      className="detail-value status-indicator"
                      style={{ 
                        color: getStatusColor(certificateData.status),
                        fontWeight: 'bold'
                      }}
                    >
                      {certificateData.status.toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Enhanced Footer Section */}
            <div className="certificate-footer">
              <div className="footer-content">
                <div className="signature-section">
                  <div className="signature-line">
                    <div className="signature-image">
                      <div className="signature-placeholder">
                        <span className="signature-text">{certificateData.authorizedBy}</span>
                      </div>
                    </div>
                    <div className="signature-details">
                      <strong>{certificateData.authorizedBy}</strong><br/>
                      <span className="signature-title">Chief Compliance Officer</span><br/>
                      <span className="issuer-name">{certificateData.issuer}</span>
                    </div>
                  </div>
                </div>
                
                <div className="verification-section">
                  <div className="qr-code-placeholder">
                    <div className="qr-content">
                      <div className="qr-pattern"></div>
                      <div className="verify-text">Scan to Verify</div>
                    </div>
                  </div>
                </div>
                
                <div className="seal-section">
                  <div className="official-seal">
                    <div className="seal-content">
                      <div className="seal-inner">
                        <div className="seal-text-top">INFRASEC</div>
                        <div className="seal-icon">⚡</div>
                        <div className="seal-text-bottom">CERTIFIED</div>
                        <div className="seal-year">{new Date(certificateData.issueDate).getFullYear()}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Security Features */}
              <div className="security-features">
                <div className="security-text">
                  This certificate contains security features to prevent counterfeiting
                </div>
                <div className="verification-code">
                  Verification Code: {certificateData.certificateNumber}-{new Date(certificateData.issueDate).getFullYear()}
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Decorative Elements */}
          <div className="certificate-decorations">
            <div className="corner-decoration top-left">
              <svg width="50" height="50" viewBox="0 0 50 50">
                <path d="M10,10 L40,10 L40,40 Z" fill="#1a365d" opacity="0.1"/>
                <circle cx="25" cy="25" r="8" fill="#3182ce" opacity="0.3"/>
              </svg>
            </div>
            <div className="corner-decoration top-right">
              <svg width="50" height="50" viewBox="0 0 50 50">
                <path d="M40,10 L10,10 L10,40 Z" fill="#1a365d" opacity="0.1"/>
                <circle cx="25" cy="25" r="8" fill="#3182ce" opacity="0.3"/>
              </svg>
            </div>
            <div className="corner-decoration bottom-left">
              <svg width="50" height="50" viewBox="0 0 50 50">
                <path d="M10,40 L40,40 L40,10 Z" fill="#1a365d" opacity="0.1"/>
                <circle cx="25" cy="25" r="8" fill="#3182ce" opacity="0.3"/>
              </svg>
            </div>
            <div className="corner-decoration bottom-right">
              <svg width="50" height="50" viewBox="0 0 50 50">
                <path d="M40,40 L10,40 L10,10 Z" fill="#1a365d" opacity="0.1"/>
                <circle cx="25" cy="25" r="8" fill="#3182ce" opacity="0.3"/>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificateTemplate;
